package diana.soleil.design.iraniancanadienbazzar.core.model

/**
 * Country dimension of the product (see `docs/US_EXPANSION_PLAN.md`). Country is a first-class filter
 * axis, chosen in onboarding alongside language and city; it lives **on the metro** (each [Metro] has a
 * [Country]) rather than as a separate nav axis — selecting a country narrows the metro list, selecting
 * a metro implies its country. Distance units localize by country: km for [CA], miles for [US].
 */
enum class Country(val code: String) {
    CA("CA"),
    US("US"),
    ;

    /** true for the imperial-distance country (US uses miles; Canada uses km). */
    val usesMiles: Boolean get() = this == US

    companion object {
        fun fromCode(code: String?): Country? =
            code?.let { c -> entries.firstOrNull { it.code.equals(c, ignoreCase = true) } }

        /**
         * Best-effort country from a device locale region (e.g. "US", "CA"). Used to preselect the
         * country in onboarding; always overridable. Unknown regions fall back to [CA] (home market).
         */
        fun fromRegion(region: String?): Country = fromCode(region) ?: CA
    }
}

/**
 * A metro/region grouping many municipalities, used for city switching (see `docs/LOCALIZATION.md`).
 * Definitions are **static app config** (names are app-owned/translatable; they change rarely); which
 * metros are actually selectable is gated at runtime by Remote Config `active_launch_cities`.
 *
 * [name] is a [LocalizedText] so the display name localizes per language; [municipalities] are matched
 * (case-insensitively) against `Business.address.city` to derive `Business.metroId`. [country] scopes
 * the metro to a country ("All \<country\>" queries filter by it) and selects the distance unit.
 */
data class Metro(
    val id: String,
    val name: LocalizedText,
    val country: Country,
    val province: String,
    val municipalities: List<String>,
    val centerLat: Double,
    val centerLng: Double,
    /**
     * Winter/standard UTC offset in hours (EST −5, CST −6, MST −7, PST −8). Both Canadian and US metros
     * observe North American DST, so open/closed hours are evaluated at this offset +1h during DST (see
     * BusinessHours).
     */
    val standardUtcOffsetHours: Int,
) {
    fun matches(city: String?): Boolean =
        city != null && municipalities.any { it.equals(city.trim(), ignoreCase = true) }
}

/**
 * All metros the app knows about. Which ones are actually selectable is gated at runtime by Remote
 * Config `active_launch_cities`; the rest render as "coming soon" in the city picker. Canadian metros
 * [GTA]/[MTL]/[YVR]/[NCR]/[YYC] are the big five; the smaller Canadian metros ([YEG] … [YYT]) were
 * launched 2026-10-06 so residents outside the big five still get a city of their own. US metros ([LA]
 * … [SD]) are seeded metro-by-metro and RC-activated only once they have credible listings
 * (`docs/US_EXPANSION_PLAN.md`).
 */
object Metros {
    val GTA = Metro(
        id = "gta",
        name = LocalizedText(en = "Greater Toronto Area", fr = "Grand Toronto", fa = "تورنتوی بزرگ"),
        country = Country.CA,
        province = "ON",
        municipalities = listOf(
            "Toronto", "North York", "Scarborough", "East York", "York", "Etobicoke",
            "Richmond Hill", "Thornhill", "Vaughan", "Markham", "Mississauga", "Brampton",
            "Oakville", "Burlington", "Aurora", "Newmarket", "Pickering", "Ajax", "Whitby",
            "Oshawa", "Milton",
        ),
        centerLat = 43.6532,
        centerLng = -79.3832,
        standardUtcOffsetHours = -5,
    )

    val MTL = Metro(
        id = "mtl",
        name = LocalizedText(en = "Greater Montréal", fr = "Grand Montréal", fa = "مونترال بزرگ"),
        country = Country.CA,
        province = "QC",
        municipalities = listOf(
            "Montréal", "Montreal", "Laval", "Verdun", "Saint-Laurent", "Westmount",
            "Pointe-Claire", "Pierrefonds", "Pierrefonds-Roxboro", "La Prairie", "Brossard",
            "Dollard-des-Ormeaux", "Côte-Saint-Luc", "Kirkland", "Dorval", "Longueuil",
            "Saint-Léonard", "Outremont",
        ),
        centerLat = 45.5019,
        centerLng = -73.5674,
        standardUtcOffsetHours = -5,
    )

    val YVR = Metro(
        id = "yvr",
        name = LocalizedText(en = "Greater Vancouver", fr = "Grand Vancouver", fa = "ونکوور بزرگ"),
        country = Country.CA,
        province = "BC",
        municipalities = listOf(
            "Vancouver", "North Vancouver", "West Vancouver", "Burnaby", "Richmond",
            "Coquitlam", "Port Coquitlam", "Port Moody", "New Westminster", "Surrey",
            "Delta", "Langley", "Maple Ridge",
        ),
        centerLat = 49.2827,
        centerLng = -123.1207,
        standardUtcOffsetHours = -8,
    )

    val NCR = Metro(
        id = "ncr",
        name = LocalizedText(en = "Ottawa–Gatineau", fr = "Ottawa–Gatineau", fa = "اتاوا–گتینو"),
        country = Country.CA,
        province = "ON",
        municipalities = listOf("Ottawa", "Gatineau", "Nepean", "Kanata", "Orléans", "Gloucester"),
        centerLat = 45.4215,
        centerLng = -75.6972,
        standardUtcOffsetHours = -5,
    )

    val YYC = Metro(
        id = "yyc",
        name = LocalizedText(en = "Calgary", fr = "Calgary", fa = "کلگری"),
        country = Country.CA,
        province = "AB",
        municipalities = listOf("Calgary", "Airdrie", "Cochrane", "Chestermere"),
        centerLat = 51.0447,
        centerLng = -114.0719,
        standardUtcOffsetHours = -7,
    )

    // ---- Smaller Canadian metros (owner decision 2026-10-06: "give each city its own"). ----
    // Each is a real community with verified Iranian businesses but far below the big-four density;
    // they are launched so a resident of Halifax or Edmonton finds *their* listings instead of an
    // All-Canada soup. Order below = rough community size.

    val YEG = Metro(
        id = "yeg",
        name = LocalizedText(en = "Edmonton", fr = "Edmonton", fa = "ادمونتون"),
        country = Country.CA,
        province = "AB",
        municipalities = listOf("Edmonton", "St. Albert", "Sherwood Park", "Spruce Grove", "Leduc"),
        centerLat = 53.5461,
        centerLng = -113.4938,
        standardUtcOffsetHours = -7,
    )

    val YWG = Metro(
        id = "ywg",
        name = LocalizedText(en = "Winnipeg", fr = "Winnipeg", fa = "وینیپگ"),
        country = Country.CA,
        province = "MB",
        municipalities = listOf("Winnipeg", "Headingley"),
        centerLat = 49.8951,
        centerLng = -97.1384,
        standardUtcOffsetHours = -6,
    )

    val YHZ = Metro(
        id = "yhz",
        name = LocalizedText(en = "Halifax", fr = "Halifax", fa = "هالیفکس"),
        country = Country.CA,
        province = "NS",
        municipalities = listOf("Halifax", "Dartmouth", "Bedford", "Lower Sackville", "Sackville"),
        centerLat = 44.6488,
        centerLng = -63.5752,
        standardUtcOffsetHours = -4,
    )

    val YYJ = Metro(
        id = "yyj",
        name = LocalizedText(en = "Victoria", fr = "Victoria", fa = "ویکتوریا"),
        country = Country.CA,
        province = "BC",
        municipalities = listOf("Victoria", "Saanich", "Langford", "Esquimalt", "Oak Bay", "Sidney"),
        centerLat = 48.4284,
        centerLng = -123.3656,
        standardUtcOffsetHours = -8,
    )

    val YKF = Metro(
        id = "ykf",
        name = LocalizedText(en = "Kitchener–Waterloo", fr = "Kitchener–Waterloo", fa = "کیچنر–واترلو"),
        country = Country.CA,
        province = "ON",
        municipalities = listOf("Kitchener", "Waterloo", "Cambridge", "Guelph"),
        centerLat = 43.4643,
        centerLng = -80.5204,
        standardUtcOffsetHours = -5,
    )

    val YXU = Metro(
        id = "yxu",
        name = LocalizedText(en = "London", fr = "London", fa = "لندن (انتاریو)"),
        country = Country.CA,
        province = "ON",
        municipalities = listOf("London", "St. Thomas"),
        centerLat = 42.9849,
        centerLng = -81.2453,
        standardUtcOffsetHours = -5,
    )

    val YHM = Metro(
        id = "yhm",
        name = LocalizedText(en = "Hamilton", fr = "Hamilton", fa = "همیلتون"),
        country = Country.CA,
        province = "ON",
        municipalities = listOf("Hamilton", "Stoney Creek", "Ancaster", "Dundas"),
        centerLat = 43.2557,
        centerLng = -79.8711,
        standardUtcOffsetHours = -5,
    )

    val YQG = Metro(
        id = "yqg",
        name = LocalizedText(en = "Windsor", fr = "Windsor", fa = "ویندزور"),
        country = Country.CA,
        province = "ON",
        municipalities = listOf("Windsor", "Tecumseh", "LaSalle"),
        centerLat = 42.3149,
        centerLng = -83.0364,
        standardUtcOffsetHours = -5,
    )

    val YGK = Metro(
        id = "ygk",
        name = LocalizedText(en = "Kingston", fr = "Kingston", fa = "کینگستون"),
        country = Country.CA,
        province = "ON",
        municipalities = listOf("Kingston"),
        centerLat = 44.2312,
        centerLng = -76.4860,
        standardUtcOffsetHours = -5,
    )

    // Saskatchewan stays on CST all year (no DST); BusinessHours adds the summer hour like everywhere
    // else, so open/closed is an hour early there in summer. Accepted for now — see docs/PENDING.md.
    val YXE = Metro(
        id = "yxe",
        name = LocalizedText(en = "Saskatoon", fr = "Saskatoon", fa = "ساسکاتون"),
        country = Country.CA,
        province = "SK",
        municipalities = listOf("Saskatoon", "Martensville", "Warman"),
        centerLat = 52.1332,
        centerLng = -106.6700,
        standardUtcOffsetHours = -6,
    )

    val YQR = Metro(
        id = "yqr",
        name = LocalizedText(en = "Regina", fr = "Regina", fa = "رجاینا"),
        country = Country.CA,
        province = "SK",
        municipalities = listOf("Regina", "White City"),
        centerLat = 50.4452,
        centerLng = -104.6189,
        standardUtcOffsetHours = -6,
    )

    // Newfoundland is UTC−3:30; the model holds whole hours, so St. John's rounds to −4 and
    // open/closed hours there run 30 minutes late. Accepted for now — see docs/PENDING.md.
    val YYT = Metro(
        id = "yyt",
        name = LocalizedText(en = "St. John's", fr = "Saint-Jean de Terre-Neuve", fa = "سنت جانز"),
        country = Country.CA,
        province = "NL",
        municipalities = listOf("St. John's", "Mount Pearl", "Paradise"),
        centerLat = 47.5615,
        centerLng = -52.7126,
        standardUtcOffsetHours = -4,
    )

    // ---- United States (docs/US_EXPANSION_PLAN.md). province holds the US state code. ----

    val LA = Metro(
        id = "la",
        name = LocalizedText(en = "Greater Los Angeles", fr = "Grand Los Angeles", fa = "لس‌آنجلس بزرگ"),
        country = Country.US,
        province = "CA",
        municipalities = listOf(
            "Los Angeles", "Westwood", "Beverly Hills", "Santa Monica", "Brentwood",
            "Glendale", "Burbank", "Pasadena", "Encino", "Sherman Oaks", "Tarzana",
            "Woodland Hills", "Irvine", "Costa Mesa", "Newport Beach", "Anaheim",
            "Santa Ana", "Tustin", "Laguna Hills", "Mission Viejo", "Torrance",
        ),
        centerLat = 34.0522,
        centerLng = -118.2437,
        standardUtcOffsetHours = -8,
    )

    val SFBA = Metro(
        id = "sfba",
        name = LocalizedText(en = "SF Bay Area", fr = "Baie de San Francisco", fa = "منطقه خلیج سان‌فرانسیسکو"),
        country = Country.US,
        province = "CA",
        municipalities = listOf(
            "San Francisco", "San Jose", "Cupertino", "Sunnyvale", "Santa Clara",
            "Mountain View", "Palo Alto", "Fremont", "Milpitas", "Campbell", "Los Gatos",
            "Oakland", "Berkeley", "San Mateo", "Redwood City", "Foster City", "Walnut Creek",
            "Pleasanton", "San Ramon", "Dublin",
        ),
        centerLat = 37.3382,
        centerLng = -121.8863,
        standardUtcOffsetHours = -8,
    )

    val DC = Metro(
        id = "dc",
        name = LocalizedText(en = "Washington DC Metro", fr = "Région de Washington", fa = "منطقه واشنگتن دی‌سی"),
        country = Country.US,
        province = "DC",
        municipalities = listOf(
            "Washington", "Bethesda", "Rockville", "Gaithersburg", "Silver Spring",
            "Potomac", "North Potomac", "Tysons", "Vienna", "McLean", "Great Falls",
            "Falls Church", "Reston", "Herndon", "Fairfax", "Arlington", "Alexandria",
            "Ashburn", "Sterling",
        ),
        centerLat = 38.9072,
        centerLng = -77.0369,
        standardUtcOffsetHours = -5,
    )

    val NYC = Metro(
        id = "nyc",
        name = LocalizedText(en = "New York Metro", fr = "Région de New York", fa = "منطقه نیویورک"),
        country = Country.US,
        province = "NY",
        municipalities = listOf(
            "New York", "Manhattan", "Brooklyn", "Queens", "Great Neck", "Roslyn",
            "Manhasset", "Port Washington", "Jericho", "Syosset", "Hicksville",
            "Fort Lee", "Englewood", "Tenafly", "Hackensack", "Paramus", "Ridgewood",
            "Jersey City", "Hoboken",
        ),
        centerLat = 40.7128,
        centerLng = -74.0060,
        standardUtcOffsetHours = -5,
    )

    val SEA = Metro(
        id = "sea",
        name = LocalizedText(en = "Seattle Metro", fr = "Région de Seattle", fa = "منطقه سیاتل"),
        country = Country.US,
        province = "WA",
        municipalities = listOf(
            "Seattle", "Bellevue", "Redmond", "Kirkland", "Bothell", "Sammamish",
            "Issaquah", "Renton", "Kent", "Mercer Island", "Woodinville", "Lynnwood",
            "Everett", "Shoreline",
        ),
        centerLat = 47.6062,
        centerLng = -122.3321,
        standardUtcOffsetHours = -8,
    )

    val HOU = Metro(
        id = "hou",
        name = LocalizedText(en = "Greater Houston", fr = "Grand Houston", fa = "هیوستون بزرگ"),
        country = Country.US,
        province = "TX",
        municipalities = listOf(
            "Houston", "Sugar Land", "Katy", "The Woodlands", "Pearland", "Cypress",
            "Spring", "Bellaire", "Pasadena", "Missouri City", "Stafford", "Richmond",
        ),
        centerLat = 29.7604,
        centerLng = -95.3698,
        standardUtcOffsetHours = -6,
    )

    val DAL = Metro(
        id = "dal",
        name = LocalizedText(en = "Dallas–Fort Worth", fr = "Dallas–Fort Worth", fa = "دالاس–فورت‌ورث"),
        country = Country.US,
        province = "TX",
        municipalities = listOf(
            "Dallas", "Plano", "Richardson", "Frisco", "Allen", "McKinney", "Irving",
            "Carrollton", "Garland", "Addison", "Fort Worth", "Arlington", "Grapevine",
            "Coppell", "Lewisville", "Flower Mound",
        ),
        centerLat = 32.7767,
        centerLng = -96.7970,
        standardUtcOffsetHours = -6,
    )

    val SD = Metro(
        id = "sd",
        name = LocalizedText(en = "San Diego", fr = "San Diego", fa = "سن‌دیگو"),
        country = Country.US,
        province = "CA",
        municipalities = listOf(
            "San Diego", "La Jolla", "Sorrento Valley", "Encinitas", "Carlsbad",
            "Del Mar", "Poway", "Rancho Bernardo", "Chula Vista", "El Cajon",
            "La Mesa", "Escondido", "Carmel Valley",
        ),
        centerLat = 32.7157,
        centerLng = -117.1611,
        standardUtcOffsetHours = -8,
    )

    /** Every metro, both countries. Order = rollout priority within each country. */
    val all: List<Metro> = listOf(
        GTA, MTL, YVR, NCR, YYC,
        YEG, YWG, YHZ, YYJ, YKF, YXU, YHM, YQG, YGK, YXE, YQR, YYT,
        LA, SFBA, DC, NYC, SEA, HOU, DAL, SD,
    )

    fun byId(id: String?): Metro? = all.firstOrNull { it.id == id }

    /**
     * The metro a [city] belongs to (matched against each metro's municipality list), optionally
     * scoped to a [country]. Used to derive a listing's metro from its address so it isn't left
     * metro-less (which would exclude it from the metro-scoped directory + featured carousel).
     */
    fun forCity(city: String?, country: Country? = null): Metro? {
        if (city.isNullOrBlank()) return null
        return all.firstOrNull { (country == null || it.country == country) && it.matches(city) }
    }

    /** Metros in [country], preserving [all] ordering (rollout priority). */
    fun forCountry(country: Country): List<Metro> = all.filter { it.country == country }

    /**
     * Countries that have at least one LAUNCHED metro (Remote Config `active_launch_cities`) — the
     * country picker only offers these, so turning off every US metro (CA-only launch) hides the US
     * option entirely rather than showing all-"coming soon". Falls back to [Country.CA] if the
     * launched set is empty/unrecognized so the picker is never empty.
     */
    fun launchedCountries(launchedMetroIds: List<String>): List<Country> {
        val launched = launchedMetroIds.map { it.lowercase() }.toSet()
        val result = Country.entries.filter { c -> forCountry(c).any { it.id.lowercase() in launched } }
        return result.ifEmpty { listOf(Country.CA) }
    }

    /**
     * City-input autocomplete: municipalities (deduped, sorted) matching [query], scoped to [country]
     * when given. Prefix matches rank before mid-string matches. Returns empty for <2 chars or an
     * exact match (so the dropdown hides once the user has picked/typed a known city).
     */
    fun suggestCities(query: String, country: Country? = null, limit: Int = 6): List<String> {
        val q = query.trim()
        if (q.length < 2) return emptyList()
        val pool = all.filter { country == null || it.country == country }
            .flatMap { it.municipalities }
            .distinct()
        if (pool.any { it.equals(q, ignoreCase = true) }) return emptyList()
        val matches = pool.filter { it.contains(q, ignoreCase = true) }
        return matches.sortedWith(
            compareByDescending<String> { it.startsWith(q, ignoreCase = true) }.thenBy { it.lowercase() },
        ).take(limit)
    }
}
